// Timezone utilities for check-in timing

export interface TimezonePreferences {
  timezone: string;
  morningStart: number; // 24-hour format (0-23)
  eveningStart: number; // 24-hour format (0-23)
  autoDetect: boolean;
}

export interface TimezoneSetting {
  name: string;
  value: string;
  offset: string;
  popular: boolean;
}

// Popular timezone options for user selection
export const POPULAR_TIMEZONES: TimezoneSetting[] = [
  { name: 'Eastern Time', value: 'America/New_York', offset: 'UTC-5/-4', popular: true },
  { name: 'Central Time', value: 'America/Chicago', offset: 'UTC-6/-5', popular: true },
  { name: 'Mountain Time', value: 'America/Denver', offset: 'UTC-7/-6', popular: true },
  { name: 'Pacific Time', value: 'America/Los_Angeles', offset: 'UTC-8/-7', popular: true },
  { name: 'London', value: 'Europe/London', offset: 'UTC+0/+1', popular: true },
  { name: 'Paris', value: 'Europe/Paris', offset: 'UTC+1/+2', popular: true },
  { name: 'Tokyo', value: 'Asia/Tokyo', offset: 'UTC+9', popular: true },
  { name: 'Sydney', value: 'Australia/Sydney', offset: 'UTC+10/+11', popular: true },
  { name: 'UTC', value: 'UTC', offset: 'UTC+0', popular: false },
];

// Default timezone preferences
export const DEFAULT_TIMEZONE_PREFERENCES: TimezonePreferences = {
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  morningStart: 6, // 6am
  eveningStart: 18, // 6pm
  autoDetect: true,
};

/**
 * Get user's current timezone
 */
export function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn('Failed to detect timezone, falling back to UTC:', error);
    return 'UTC';
  }
}

/**
 * Get current time in user's timezone
 */
export function getCurrentTimeInTimezone(timezone: string): Date {
  try {
    // Create a date in the target timezone
    const now = new Date();
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
    
    // Get timezone offset
    const targetTime = new Date(utc + (getTimezoneOffset(timezone) * 60000));
    return targetTime;
  } catch (error) {
    console.warn('Failed to get time in timezone, using local time:', error);
    return new Date();
  }
}

/**
 * Get timezone offset in minutes
 */
function getTimezoneOffset(timezone: string): number {
  try {
    const now = new Date();
    const utc = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const target = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    return (target.getTime() - utc.getTime()) / (1000 * 60);
  } catch (error) {
    console.warn('Failed to calculate timezone offset:', error);
    return 0;
  }
}

/**
 * Determine if it's currently morning or evening based on user preferences
 */
export function getTimePeriod(currentTime: Date, preferences: TimezonePreferences): 'morning' | 'afternoon' | 'evening' | 'night' {
  const currentTimeInTimezone = getCurrentTimeInTimezone(preferences.timezone);
  const currentHour = currentTimeInTimezone.getHours();
  
  const { morningStart, eveningStart } = preferences;
  
  // Determine time period based on hour
  if (currentHour >= morningStart && currentHour < 12) {
    return 'morning';
  } else if (currentHour >= 12 && currentHour < eveningStart) {
    return 'afternoon';
  } else if (currentHour >= eveningStart && currentHour < 22) {
    return 'evening';
  } else {
    return 'night';
  }
}

/**
 * Format timezone for display
 */
export function formatTimezone(timezone: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    
    const parts = formatter.formatToParts(now);
    const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value || timezone;
    
    return `${timezone.replace('_', ' ')} (${timeZoneName})`;
  } catch (error) {
    return timezone;
  }
}

/**
 * Get user-friendly time period description
 */
export function getTimePeriodDescription(preferences: TimezonePreferences): string {
  const currentTime = new Date();
  const timePeriod = getTimePeriod(currentTime, preferences);
  
  if (timePeriod === 'morning') {
    return `Morning (${preferences.morningStart}:00 - 12:00)`;
  } else if (timePeriod === 'afternoon') {
    return `Afternoon (12:00 - ${preferences.eveningStart}:00)`;
  } else if (timePeriod === 'evening') {
    return `Evening (${preferences.eveningStart}:00 - 22:00)`;
  } else {
    return `Night (22:00 - ${preferences.morningStart}:00)`;
  }
}

/**
 * Validate timezone preferences
 */
export function validateTimezonePreferences(preferences: Partial<TimezonePreferences>): TimezonePreferences {
  return {
    timezone: preferences.timezone || getUserTimezone(),
    morningStart: Math.max(0, Math.min(23, preferences.morningStart ?? 6)),
    eveningStart: Math.max(0, Math.min(23, preferences.eveningStart ?? 18)),
    autoDetect: preferences.autoDetect ?? true,
  };
}

/**
 * Check if timezone auto-detection is supported
 */
export function isTimezoneDetectionSupported(): boolean {
  try {
    return Boolean(Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone);
  } catch {
    return false;
  }
}